/**
 * `applyWidgetTheme` — apply an org's `OrgTheme` to the widget as `--lc-*` CSS
 * variables (ARCHITECTURE §7).
 *
 * Thin wrapper over the shared `applyTheme()` that targets the widget's own root
 * element (`:root` inside the isolated iframe document) rather than the host
 * page. Because the widget's Tailwind config reads the same `--lc-*` vars, org
 * branding applies with zero component changes. `WidgetConfig.theme` is the
 * source; missing fields fall back to `DEFAULT_THEME`.
 */

import { applyTheme, DEFAULT_THEME } from '@flozic/shared/theme';
import type { OrgTheme, WidgetConfig } from '@flozic/shared/types';

/**
 * Apply the theme from a loaded `WidgetConfig` (or a raw `OrgTheme`) to the
 * widget document root. Returns a teardown that removes the applied vars.
 *
 * @param theme  the org theme (or the full widget config); undefined → defaults.
 * @param target the element to write vars onto; defaults to `document.documentElement`.
 */
export function applyWidgetTheme(
  theme: OrgTheme | WidgetConfig | undefined,
  target?: HTMLElement,
): () => void {
  const orgTheme: Partial<OrgTheme> | undefined = theme
    ? 'theme' in theme
      ? theme.theme
      : theme
    : undefined;
  return applyTheme(orgTheme ?? DEFAULT_THEME, target);
}
